/**
 * The send defaults the Options tab edits (creative.dropDefaultsJson) and
 * their merge rule: batches and SMSWiser AI optimization are mutually
 * exclusive — turning one on clears the other, in one place. The shortlink
 * format defaults to SMSWiser's own (path / none).
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DEFAULT_CLICK_ID_STRUCTURE,
  DEFAULT_DOMAIN_SCHEME,
  isShortlinkClickIdStructure,
  isShortlinkDomainScheme,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

export interface DraftSendDefaults {
  forceSend: boolean;
  useOnlyNewDomains: boolean;
  /** null = Auto (SMSWiser picks the domain). */
  shortLinkTldValue: string | null;
  clickIdStructure: ShortlinkClickIdStructure;
  domainScheme: ShortlinkDomainScheme;
  batchAmount: number | null;
  batchDelay: number | null;
  aiOptimizationEnabled: boolean;
}

export function mergeSendDefaults(
  current: DraftSendDefaults,
  patch: Partial<DraftSendDefaults>,
): DraftSendDefaults {
  const next = { ...current, ...patch };
  if (patch.aiOptimizationEnabled === true) {
    next.batchAmount = null;
    next.batchDelay = null;
  }
  if (
    (patch.batchAmount != null && patch.batchAmount > 0) ||
    (patch.batchDelay != null && patch.batchDelay > 0)
  ) {
    next.aiOptimizationEnabled = false;
  }
  return next;
}

export const DEFAULT_SEND: DraftSendDefaults = {
  forceSend: false,
  useOnlyNewDomains: false,
  shortLinkTldValue: null,
  clickIdStructure: DEFAULT_CLICK_ID_STRUCTURE,
  domainScheme: DEFAULT_DOMAIN_SCHEME,
  batchAmount: null,
  batchDelay: null,
  aiOptimizationEnabled: false,
};
const EDITED_SEND_KEYS = new Set<string>([
  'forceSend', 'useOnlyNewDomains', 'shortLinkTldValue', 'clickIdStructure', 'domainScheme', 'batchAmount', 'batchDelay',
  'aiOptimizationEnabled',
]);
/** The send-option keys the Options tab does NOT edit, kept verbatim. */
export function rawSendDefaults(json: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(json).filter(([k]) => !EDITED_SEND_KEYS.has(k)));
}
export function pickSendDefaults(json: Record<string, unknown>): Partial<DraftSendDefaults> {
  const out: Partial<DraftSendDefaults> = {};
  if (typeof json.forceSend === 'boolean') out.forceSend = json.forceSend;
  if (typeof json.useOnlyNewDomains === 'boolean')
    out.useOnlyNewDomains = json.useOnlyNewDomains;
  if (typeof json.shortLinkTldValue === 'string')
    out.shortLinkTldValue = json.shortLinkTldValue;
  if (isShortlinkClickIdStructure(json.clickIdStructure)) out.clickIdStructure = json.clickIdStructure;
  if (isShortlinkDomainScheme(json.domainScheme)) out.domainScheme = json.domainScheme;
  if (typeof json.batchAmount === 'number') out.batchAmount = json.batchAmount;
  if (typeof json.batchDelay === 'number') out.batchDelay = json.batchDelay;
  if (typeof json.aiOptimizationEnabled === 'boolean')
    out.aiOptimizationEnabled = json.aiOptimizationEnabled;
  return out;
}
